import { useState, useEffect } from 'react';

/**
 * Formats seconds into MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string (MM:SS)
 */
export const formatTime = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  // Pad minutes so width stays stable through 99:59 (Observer / header layout).
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * Merge a remote ascending timer (meeting clock) without small backward jumps from sync lag.
 */
export function mergeRemoteAscendingTimer(prev, remote, maxLagSeconds = 3) {
  if (typeof remote !== 'number' || Number.isNaN(remote)) return prev;
  if (typeof prev !== 'number' || Number.isNaN(prev)) return remote;
  if (remote >= prev) return remote;
  if (prev - remote > maxLagSeconds) return remote;
  return prev;
}

/**
 * Merge a remote descending timer (speaker clock) without small upward jumps from sync lag.
 */
export function mergeRemoteDescendingTimer(prev, remote, maxLagSeconds = 3) {
  if (typeof remote !== 'number' || Number.isNaN(remote)) return prev;
  if (typeof prev !== 'number' || Number.isNaN(prev)) return remote;
  if (remote <= prev) return remote;
  if (remote - prev > maxLagSeconds) return remote;
  return prev;
}

/**
 * Creates a timer hook for managing time state
 * @param {number} initialTime - Initial time in seconds
 * @returns {object} Timer state and controls
 */
export const useTimer = (initialTime = 0) => {
  const [time, setTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const start = () => setIsRunning(true);
  const pause = () => setIsRunning(false);
  const stop = () => {
    setIsRunning(false);
    setTime(initialTime);
  };
  const reset = () => setTime(initialTime);

  return {
    time,
    isRunning,
    start,
    pause,
    stop,
    reset,
    setTime,
    formattedTime: formatTime(time),
  };
};
